import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { API_ENDPOINTS } from '../../config/api';
import api from '../../utils/apiClient';
import { motion } from 'framer-motion';
import {
  BAIIcon,
  BNBIcon,
  IMXIcon,
  USDTIcon,
  currencyColors
} from '../CurrencyIcons';

const CurrencyOption = ({ value, selected, onChange, children }) => {
  const colors = currencyColors[value] || currencyColors.BAI;

  return (
    <div
      className={`relative flex items-center p-3 rounded-lg cursor-pointer transition-all ${
        selected
          ? `bg-gradient-to-r ${colors.gradient}/20 border-2 ${colors.border}`
          : 'bg-dark-400/50 border border-gray-700 hover:border-gray-500'
      }`}
      onClick={() => onChange(value)}
    >
      <input
        type="radio"
        name="currency"
        value={value}
        checked={selected}
        onChange={() => onChange(value)}
        className="sr-only"
      />
      {children}
      {selected && (
        <div className="absolute top-0 right-0 -mt-2 -mr-2">
          <span className={`flex h-4 w-4 rounded-full ${colors.bgLight}`}>
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75"></span>
            <span className={`relative inline-flex rounded-full h-4 w-4 bg-gradient-to-r ${colors.gradient}`}></span>
          </span>
        </div>
      )}
    </div>
  );
};

const CreateQuest = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [currency, setCurrency] = useState('BAI');

  useEffect(() => {
    // Wait for user info to be loaded
    if (user !== null) {
      setInitializing(false);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.isConnected) {
      toast.error('Please connect your wallet to create a quest');
      return;
    }

    if (!user?.isAdmin) {
      toast.error('Admin privileges required to create quests');
      return;
    }

    setLoading(true);
    try {
      const formData = {
        title: e.target.title.value,
        description: e.target.description.value,
        difficulty: e.target.difficulty.value,
        reward: {
          amount: parseFloat(e.target.reward.value),
          currency: currency
        },
        image: e.target.image.value || undefined,
        gameUrl: '/bitpool/index.html'
      };

      const response = await api.post(API_ENDPOINTS.quests.create, formData);

      if (response.questId) {
        toast.success('Quest created successfully!');
        navigate(`/quests/${response.questId}`);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error creating quest:', error);
      if (error.response?.data?.code === 'MISSING_FIELDS') {
        toast.error('Please fill in all required fields');
      } else if (error.response?.data?.code === 'NOT_ADMIN') {
        toast.error('Admin privileges required');
      } else {
        toast.error('Failed to create quest');
      }
    } finally {
      setLoading(false);
    }
  };

  if (initializing) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading user info...</p>
        </div>
      </div>
    );
  }

  if (!user?.isConnected || !user?.address) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Connect Your Wallet</h2>
          <p className="text-gray-400 mb-6">Please connect your wallet to create a quest</p>
          {/* @ts-expect-error Temporary fix for React 19 */}
          <appkit-button />
        </div>
      </div>
    );
  }

  if (!user.isAdmin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Admin Access Required</h2>
          <p className="text-gray-400 mb-6">You need admin privileges to create quests</p>
          <button
            onClick={() => navigate('/quests')}
            className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded"
          >
            Back to Quests
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Create Quest</h1>
          <button
            onClick={() => navigate('/quests')}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded"
          >
            Back to Quests
          </button>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="title" className="block text-gray-300 mb-2">
                Quest Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                required
                className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300 focus:outline-none focus:border-primary"
                placeholder="Enter quest title"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-gray-300 mb-2">Description</label>
              <textarea
                id="description"
                name="description"
                rows={3}
                className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300 focus:outline-none focus:border-primary"
                placeholder="Enter quest description"
              />
            </div>

            <div>
              <label htmlFor="difficulty" className="block text-gray-300 mb-2">
                Difficulty <span className="text-red-500">*</span>
              </label>
              <select
                id="difficulty"
                name="difficulty"
                required
                className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300 focus:outline-none focus:border-primary"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>

            <div>
              <label htmlFor="reward" className="block text-gray-300 mb-2">
                Reward Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="reward"
                name="reward"
                required
                min="0"
                step="0.01"
                className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300 focus:outline-none focus:border-primary"
                placeholder="Enter reward amount"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-3">
                Reward Currency <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <CurrencyOption
                  value="BAI"
                  selected={currency === 'BAI'}
                  onChange={setCurrency}
                >
                  <BAIIcon size="md" className="mr-2" />
                  <span className="text-white font-medium">BAI</span>
                </CurrencyOption>

                <CurrencyOption
                  value="BNB"
                  selected={currency === 'BNB'}
                  onChange={setCurrency}
                >
                  <BNBIcon size="md" className="mr-2" />
                  <span className="text-white font-medium">BNB</span>
                </CurrencyOption>

                <CurrencyOption
                  value="IMX"
                  selected={currency === 'IMX'}
                  onChange={setCurrency}
                >
                  <IMXIcon size="md" className="mr-2" />
                  <span className="text-white font-medium">IMX</span>
                </CurrencyOption>

                <CurrencyOption
                  value="USDT"
                  selected={currency === 'USDT'}
                  onChange={setCurrency}
                >
                  <USDTIcon size="md" className="mr-2" />
                  <span className="text-white font-medium">USDT</span>
                </CurrencyOption>
              </div>
            </div>

            <div>
              <label htmlFor="image" className="block text-gray-300 mb-2">Image URL</label>
              <input
                type="url"
                id="image"
                name="image"
                className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300 focus:outline-none focus:border-primary"
                placeholder="https://example.com/image.jpg"
              />
              <p className="text-sm text-gray-500 mt-1">Leave empty to use default image</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded transition-colors ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Creating Quest...' : 'Create Quest'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateQuest;
